// Registration and update plumbing for service-worker.js.
//
// The worker never activates itself. This module spots a build waiting in the
// wings, tells the app about it, and only hands over when the app calls
// applyUpdate() — which the app does by itself only when nobody is using it,
// otherwise after asking; see hooks/useServiceWorkerUpdate.js.

const SW_URL = "/service-worker.js";

let waitingWorker = null;
let swRegistration = null;
let reloading = false;

// A SET of subscribers, not one slot. Two surfaces now watch this — the update
// toast and the changelog/version badge — and with a single slot the second to
// mount silently replaced the first, so whichever lost the race never heard
// about a waiting build.
const subscribers = new Set();

const isSupported = () =>
  "serviceWorker" in navigator && process.env.NODE_ENV === "production";

function announce(worker) {
  waitingWorker = worker;
  for (const subscriber of subscribers) subscriber(worker);
}

/**
 * Subscribe to "a new build is installed and waiting".
 *
 * Fires immediately if one was already waiting when the subscriber attached —
 * the worker can finish installing well before React mounts, and that signal
 * would otherwise be lost.
 */
export function onUpdateReady(callback) {
  subscribers.add(callback);
  if (waitingWorker) callback(waitingWorker);

  return () => {
    subscribers.delete(callback);
  };
}

/** True when a new build is installed and waiting to be taken. */
export const isUpdateWaiting = () => !!waitingWorker;

// How often an app left open re-checks, and the floor between any two checks
// (focus and visibility often fire together on resume).
const UPDATE_POLL_MS = 10 * 60 * 1000;
const MIN_CHECK_GAP_MS = 30 * 1000;
let lastCheckAt = 0;

/**
 * Ask the browser to re-check for a new worker now.
 *
 * The changelog is where a user goes to find out what is new, so opening it is
 * exactly the moment to look — rather than waiting for the next tab focus.
 */
export function checkForUpdate() {
  if (!swRegistration) return Promise.resolve();
  lastCheckAt = Date.now();
  return swRegistration.update().catch(() => {});
}

// Background triggers share a throttle; an explicit checkForUpdate() doesn't.
function checkSoon() {
  if (Date.now() - lastCheckAt < MIN_CHECK_GAP_MS) return;
  checkForUpdate();
}

/**
 * Take the newest build, whatever state we are in.
 *
 * With a worker already waiting this is applyUpdate(). Without one it re-checks
 * first, then either hands over or — when there is no service worker in play at
 * all (unsupported browser, dev) — falls back to a plain reload, which in that
 * case genuinely does fetch the new bundle.
 */
export async function requestUpdate() {
  if (waitingWorker) {
    applyUpdate();
    return;
  }
  await checkForUpdate();
  if (waitingWorker) {
    applyUpdate();
    return;
  }
  if (!reloading) {
    reloading = true;
    window.location.reload();
  }
}

/**
 * Hand over to the waiting build.
 *
 * The reload waits for controllerchange: reloading before the new worker has
 * taken control would just re-run the old shell and lose the update again.
 */
export function applyUpdate() {
  if (!waitingWorker || reloading) return;

  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    },
    { once: true },
  );

  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}

// Announce `worker` once it reaches `installed` — immediately if it already
// has. Only an update is announced: with no controller this is the first
// install, and a shell quietly being cached is invisible, and good.
function watchInstalling(worker) {
  if (!worker) return;
  const check = () => {
    if (worker.state === "installed" && navigator.serviceWorker.controller) {
      announce(worker);
    }
  };
  worker.addEventListener("statechange", check);
  check();
}

export function register() {
  if (!isSupported()) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      // updateViaCache "none": the update check always goes to the network,
      // never the HTTP cache, whatever headers a proxy put on the worker.
      .register(SW_URL, { updateViaCache: "none" })
      .then((registration) => {
        swRegistration = registration;
        lastCheckAt = Date.now(); // register() just checked.

        // Left waiting by an earlier visit where the prompt was never actioned.
        if (registration.waiting && navigator.serviceWorker.controller) {
          announce(registration.waiting);
        }
        // Already downloading when we got here: the updatefound for it has
        // been and gone, so watch it directly or it is never announced.
        watchInstalling(registration.installing);

        registration.addEventListener("updatefound", () => {
          watchInstalling(registration.installing);
        });

        // An installed PWA can sit open for days, and Android resumes it
        // without a navigation, which is the only time the browser checks by
        // itself. So check on every way back into the app, and on a timer
        // while it stays open.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") checkSoon();
        });
        window.addEventListener("focus", checkSoon);
        window.addEventListener("online", checkSoon);
        window.addEventListener("pageshow", (event) => {
          if (event.persisted) checkSoon();
        });
        setInterval(() => {
          if (document.visibilityState === "visible") checkSoon();
        }, UPDATE_POLL_MS);
      })
      .catch(() => {});
  });
}
